import { makeAutoObservable } from "mobx";
import { ProductInstance, ProductInstancePart } from "../ProductInstance";
import { ProductInstanceCreatorState } from "./ProductInstanceCreatorState";
/*
 * Utility class for building correct `ProductInstance` - it deals with changing productTypes in halves / splitting / removal of halves
 */
export class ProductInstanceCreator {
    constructor(params) {
        this.productInstanceCreatorStateFactory =
            params.productInstanceCreatorStateFactory;
        this.possibleProductTypeIds = params.possibleProductTypeIds;
        this._state = params.state;
        this.trackingId = params.trackingId;
        makeAutoObservable(this);
    }
    get state() {
        return this._state;
    }
    get productTypeIds() {
        return this._state.productTypeIds;
    }
    setState(newState) {
        this._state = newState;
        this.state.selectInferredDefaults();
    }
    selectInferredDefaults() {
        this.state.selectInferredDefaults();
    }
    get instance() {
        return new ProductInstance({
            customParameterInstances: this.state.commonCustomParameterInstances,
            commonModifierInstances: this.state.commonModifierInstances,
            parts: this.state.productInstanceParts,
            productPackagingFeeId: this.productPackagingFeeId,
        });
    }
    get productPackagingFeeId() {
        return this.state.productTypePackagingFeeCreator.selectedPackagingFeeTypeId;
    }
    split() {
        const { partCreator, productCategory } = this
            .state;
        this.setState(this.productInstanceCreatorStateFactory.createHalves({
            productCategory,
            initialCommonModifierInstances: this.state.commonModifierInstances,
            initialCommonCustomParameterInstances: this.state.commonCustomParameterInstances,
            firstHalf: partCreator.productInstancePart,
            secondHalf: partCreator.productInstancePart,
            productPackagingFeeId: this.productPackagingFeeId,
        }));
    }
    removeHalf(half) {
        if (!ProductInstanceCreatorState.isSplit(this.state)) {
            throw new Error("Removing part is not possible at this state");
        }
        const partCreator = this.state[invertHalf(half)];
        this.setState(this.productInstanceCreatorStateFactory.createSingle({
            productCategory: this.state.productCategory,
            initialCommonModifierInstances: this.state.commonModifierInstances,
            initialCommonCustomParameterInstances: this.state.commonCustomParameterInstances,
            part: partCreator.productInstancePart,
            productPackagingFeeId: this.productPackagingFeeId,
        }));
    }
    removeFirstHalf() {
        this.removeHalf("firstHalf");
    }
    removeSecondHalf() {
        this.removeHalf("secondHalf");
    }
    setPartProductType(productType) {
        if (ProductInstanceCreatorState.isSplit(this.state)) {
            throw new Error("Use setFirstHalfProductType/setSecondHalfProductType in split state");
        }
        this.setState(this.productInstanceCreatorStateFactory.createSingle({
            productCategory: this.state.productCategory,
            initialCommonModifierInstances: this.state.commonModifierInstances,
            initialCommonCustomParameterInstances: this.state.commonCustomParameterInstances,
            part: ProductInstancePart.empty(productType),
            productPackagingFeeId: this.productPackagingFeeId,
        }));
    }
    setFirstHalfProductType(productType) {
        if (!ProductInstanceCreatorState.isSplit(this.state)) {
            throw new Error("Use setPartProductType in non-split state");
        }
        this.setState(this.productInstanceCreatorStateFactory.createHalves({
            productCategory: this.state.productCategory,
            initialCommonModifierInstances: this.state.commonModifierInstances,
            initialCommonCustomParameterInstances: this.state.commonCustomParameterInstances,
            firstHalf: ProductInstancePart.empty(productType),
            secondHalf: this.state.secondHalf.productInstancePart,
            productPackagingFeeId: this.productPackagingFeeId,
        }));
    }
    setSecondHalfProductType(productType) {
        if (!ProductInstanceCreatorState.isSplit(this.state)) {
            throw new Error("Use setPartProductType in non-split state");
        }
        this.setState(this.productInstanceCreatorStateFactory.createHalves({
            productCategory: this.state.productCategory,
            initialCommonModifierInstances: this.state.commonModifierInstances,
            initialCommonCustomParameterInstances: this.state.commonCustomParameterInstances,
            firstHalf: this.state.firstHalf.productInstancePart,
            secondHalf: ProductInstancePart.empty(productType),
            productPackagingFeeId: this.productPackagingFeeId,
        }));
    }
}
function invertHalf(half) {
    switch (half) {
        case "firstHalf":
            return "secondHalf";
        case "secondHalf":
            return "firstHalf";
    }
}
